import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Phone, PhoneMissed, PhoneIncoming, Clock, TrendingUp, TrendingDown, 
  MapPin, Tag, RefreshCw, Shield, BarChart3, PieChart, Activity,
  Calendar, Users, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const regionConfig = {
  JAX: { name: 'Jacksonville', color: '#3b82f6' },
  ORL: { name: 'Orlando', color: '#8b5cf6' },
  TAM: { name: 'Tampa', color: '#22c55e' },
  WPB: { name: 'West Palm Beach', color: '#f97316' },
  NAP: { name: 'Naples', color: '#06b6d4' },
  MIA: { name: 'Miami', color: '#ec4899' }
};

const sourceColors = {
  'TV': '#ef4444',
  'Google': '#4285f4',
  'Google LSA': '#34a853',
  'Meta': '#1877f2',
  'Website': '#6366f1',
  'Yard Sign': '#22c55e',
  'Referral': '#f59e0b',
  'Direct': '#64748b',
  'Other': '#94a3b8'
};

export default function CallCenterDashboards() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [sources, setSources] = useState([]);
  const [regions, setRegions] = useState([]);
  const [status, setStatus] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [dailyTrends, setDailyTrends] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sourcesRes, regionsRes, statusRes] = await Promise.all([
        axios.get(`${API}/settings/call-center/sources`, { headers }),
        axios.get(`${API}/settings/call-center/regions`, { headers }),
        axios.get(`${API}/settings/call-center/status`, { headers })
      ]);
      
      setSources(sourcesRes.data.sources || []);
      setRegions(regionsRes.data.regions || []);
      setStatus(statusRes.data);
      
      // Generate simulated hourly data for now (will come from API)
      const hours = Array.from({ length: 24 }, (_, i) => ({
        hour: i,
        label: `${i.toString().padStart(2, '0')}:00`,
        total: Math.floor(Math.random() * 20) + 5,
        missed: Math.floor(Math.random() * 5),
        answered: Math.floor(Math.random() * 15) + 5
      }));
      setHourlyData(hours);
      
      // Generate daily trends
      const days = Array.from({ length: parseInt(dateRange) }, (_, i) => {
        const date = subDays(new Date(), parseInt(dateRange) - 1 - i);
        return {
          date: format(date, 'MMM d'),
          total: Math.floor(Math.random() * 50) + 30,
          answered: Math.floor(Math.random() * 40) + 25,
          missed: Math.floor(Math.random() * 10) + 2
        };
      });
      setDailyTrends(days);
      
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const totalCalls = sources.reduce((sum, s) => sum + (s.total_calls || 0), 0);
  const totalAnswered = sources.reduce((sum, s) => sum + (s.answered || 0), 0);
  const totalMissed = sources.reduce((sum, s) => sum + (s.missed || 0), 0);
  const overallAnswerRate = totalCalls > 0 ? ((totalAnswered / totalCalls) * 100).toFixed(1) : 0;

  // Find top source
  const topSource = sources.length > 0 ? sources.reduce((a, b) => (a.total_calls || 0) > (b.total_calls || 0) ? a : b) : null;
  
  // Find region with most missed calls
  const highestMissedRegion = regions.length > 0 ? regions.reduce((a, b) => (a.missed || 0) > (b.missed || 0) ? a : b) : null;

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Safe Mode Banner */}
        <Alert className="bg-blue-50 border-blue-200">
          <Shield className="h-5 w-5 text-blue-600" />
          <AlertDescription className="ml-2">
            <span className="font-semibold text-blue-800">Safe Mode Active</span>
            <span className="text-blue-700 ml-2">
              Viewing read-only call analytics from CallRail. All data is for observation only.
            </span>
          </AlertDescription>
        </Alert>

        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Source & Volume Dashboards</h1>
            <p className="text-muted-foreground">
              Analyze call patterns by source, region, and time • {status?.total_interactions || 0} total calls tracked
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchDashboardData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Calls</p>
                  <p className="text-3xl font-bold">{totalCalls.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">Last {dateRange} days</p>
                </div>
                <Phone className="h-10 w-10 text-primary opacity-30" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Answer Rate</p>
                  <p className="text-3xl font-bold text-green-600">{overallAnswerRate}%</p>
                  <div className="flex items-center gap-1 mt-1">
                    {parseFloat(overallAnswerRate) >= 80 ? (
                      <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-red-500" />
                    )}
                    <span className="text-xs text-muted-foreground">
                      {totalAnswered.toLocaleString()} answered
                    </span>
                  </div>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Missed Calls</p>
                  <p className="text-3xl font-bold text-red-600">{totalMissed.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {totalCalls > 0 ? ((totalMissed / totalCalls) * 100).toFixed(1) : 0}% of total
                  </p>
                </div>
                <PhoneMissed className="h-10 w-10 text-red-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Top Source</p>
                  <p className="text-xl font-bold">{topSource?.source || '-'}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {topSource?.total_calls?.toLocaleString() || 0} calls
                  </p>
                </div>
                <Tag className="h-10 w-10 text-purple-500 opacity-30" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="sources" className="space-y-4">
          <TabsList>
            <TabsTrigger value="sources">
              <Tag className="h-4 w-4 mr-2" />
              By Source
            </TabsTrigger>
            <TabsTrigger value="regions">
              <MapPin className="h-4 w-4 mr-2" />
              By Region
            </TabsTrigger>
            <TabsTrigger value="trends">
              <Activity className="h-4 w-4 mr-2" />
              Trends
            </TabsTrigger>
            <TabsTrigger value="hourly">
              <Clock className="h-4 w-4 mr-2" />
              By Hour
            </TabsTrigger>
          </TabsList>

          {/* Sources Tab */}
          <TabsContent value="sources">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sources Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Calls by Source</CardTitle>
                  <CardDescription>Volume distribution across marketing channels</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">Loading...</div>
                  ) : sources.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      No source data available
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {sources.slice(0, 10).map((source) => {
                        const percentage = totalCalls > 0 ? (source.total_calls / totalCalls) * 100 : 0;
                        return (
                          <div key={source.source} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">{source.source}</span>
                              <span className="text-muted-foreground">
                                {source.total_calls?.toLocaleString()} ({percentage.toFixed(1)}%)
                              </span>
                            </div>
                            <div className="h-3 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${percentage}%`,
                                  backgroundColor: sourceColors[source.source] || '#64748b'
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sources Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Source Performance</CardTitle>
                  <CardDescription>Answer rates and average duration by source</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">Loading...</div>
                  ) : (
                    <div className="overflow-auto max-h-80">
                      <table className="w-full text-sm">
                        <thead className="border-b">
                          <tr className="text-muted-foreground">
                            <th className="text-left py-2">Source</th>
                            <th className="text-right py-2">Calls</th>
                            <th className="text-right py-2">Answered</th>
                            <th className="text-right py-2">Missed</th>
                            <th className="text-right py-2">Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sources.map((source) => (
                            <tr key={source.source} className="border-b last:border-0">
                              <td className="py-2 font-medium">{source.source}</td>
                              <td className="text-right py-2">{source.total_calls?.toLocaleString()}</td>
                              <td className="text-right py-2 text-green-600">{source.answered?.toLocaleString()}</td>
                              <td className="text-right py-2 text-red-600">{source.missed?.toLocaleString()}</td>
                              <td className="text-right py-2">
                                <Badge 
                                  variant={source.answer_rate >= 80 ? 'default' : source.answer_rate >= 60 ? 'secondary' : 'destructive'}
                                >
                                  {source.answer_rate?.toFixed(0)}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Regions Tab */}
          <TabsContent value="regions">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Regions Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Calls by Region</CardTitle>
                  <CardDescription>Volume distribution across service areas</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="h-64 flex items-center justify-center">Loading...</div>
                  ) : regions.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      No region data available
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {regions.map((region) => {
                        const totalRegion = regions.reduce((sum, r) => sum + (r.total_calls || 0), 0);
                        const percentage = totalRegion > 0 ? (region.total_calls / totalRegion) * 100 : 0;
                        const config = regionConfig[region.region_code] || { name: region.region_code, color: '#64748b' };
                        
                        return (
                          <div key={region.region_code} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">{region.region_name || config.name}</span>
                              <span className="text-muted-foreground">
                                {region.total_calls?.toLocaleString()} ({percentage.toFixed(1)}%)
                              </span>
                            </div>
                            <div className="h-3 bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${percentage}%`,
                                  backgroundColor: config.color
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Region Cards */}
              <div className="space-y-4">
                {regions.map((region) => {
                  const config = regionConfig[region.region_code] || { name: region.region_code, color: '#64748b' };
                  return (
                    <Card key={region.region_code}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-10 rounded"
                              style={{ backgroundColor: config.color }}
                            />
                            <div>
                              <h4 className="font-semibold">{region.region_name || config.name}</h4>
                              <p className="text-sm text-muted-foreground">{region.region_code}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold">{region.total_calls?.toLocaleString()}</div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-green-600">{region.answered} answered</span>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-red-600">{region.missed} missed</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {highestMissedRegion && highestMissedRegion.missed > 0 && (
                  <Alert className="bg-orange-50 border-orange-200">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="ml-2 text-orange-800">
                      <strong>{regionConfig[highestMissedRegion.region_code]?.name || highestMissedRegion.region_code}</strong> has the highest missed call rate 
                      ({highestMissedRegion.missed} missed calls). Consider increasing coverage.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends">
            <Card>
              <CardHeader>
                <CardTitle>Daily Call Trends</CardTitle>
                <CardDescription>Answered vs missed calls over time</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 flex items-center justify-center">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Simple visual representation */}
                    <div className="flex items-end gap-1 h-48 border-b pb-2">
                      {dailyTrends.map((day, index) => {
                        const maxTotal = Math.max(...dailyTrends.map(d => d.total));
                        const heightPercent = maxTotal > 0 ? (day.total / maxTotal) * 100 : 0;
                        const answeredPercent = day.total > 0 ? (day.answered / day.total) * heightPercent : 0;
                        
                        return (
                          <div key={index} className="flex-1 flex flex-col items-center group relative">
                            <div 
                              className="w-full rounded-t transition-all hover:opacity-80"
                              style={{ height: `${heightPercent}%`, minHeight: '4px' }}
                            >
                              <div 
                                className="w-full bg-green-500 rounded-t"
                                style={{ height: `${(answeredPercent / heightPercent) * 100}%` }}
                              />
                              <div 
                                className="w-full bg-red-400"
                                style={{ height: `${((heightPercent - answeredPercent) / heightPercent) * 100}%` }}
                              />
                            </div>
                            {/* Tooltip */}
                            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover border rounded-lg p-2 shadow-lg z-10 text-xs whitespace-nowrap">
                              <p className="font-medium">{day.date}</p>
                              <p className="text-green-600">Answered: {day.answered}</p>
                              <p className="text-red-600">Missed: {day.missed}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{dailyTrends[0]?.date}</span>
                      <span>{dailyTrends[Math.floor(dailyTrends.length / 2)]?.date}</span>
                      <span>{dailyTrends[dailyTrends.length - 1]?.date}</span>
                    </div>
                    <div className="flex items-center justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-green-500" />
                        <span>Answered</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-red-400" />
                        <span>Missed</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hourly Tab */}
          <TabsContent value="hourly">
            <Card>
              <CardHeader>
                <CardTitle>Calls by Hour of Day</CardTitle>
                <CardDescription>Identify peak call times and coverage needs</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 flex items-center justify-center">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {/* Hourly bars */}
                    <div className="flex items-end gap-1 h-48 border-b pb-2">
                      {hourlyData.map((hour) => {
                        const maxTotal = Math.max(...hourlyData.map(h => h.total));
                        const heightPercent = maxTotal > 0 ? (hour.total / maxTotal) * 100 : 0;
                        const isBusiness = hour.hour >= 8 && hour.hour <= 18;
                        
                        return (
                          <div 
                            key={hour.hour} 
                            className="flex-1 flex flex-col items-center group relative"
                          >
                            <div
                              className={`w-full rounded-t transition-all ${
                                isBusiness ? 'bg-primary' : 'bg-muted-foreground/50'
                              } hover:opacity-80`}
                              style={{ height: `${heightPercent}%`, minHeight: '4px' }}
                            />
                            {/* Tooltip */}
                            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-popover border rounded-lg p-2 shadow-lg z-10 text-xs whitespace-nowrap">
                              <p className="font-medium">{hour.label}</p>
                              <p>Total: {hour.total}</p>
                              <p className="text-green-600">Answered: {hour.answered}</p>
                              <p className="text-red-600">Missed: {hour.missed}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>12am</span>
                      <span>6am</span>
                      <span>12pm</span>
                      <span>6pm</span>
                      <span>12am</span>
                    </div>
                    <div className="flex items-center justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-primary" />
                        <span>Business Hours</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-muted-foreground/50" />
                        <span>After Hours</span>
                      </div>
                    </div>
                    
                    {/* Peak hours insight */}
                    <Alert>
                      <Clock className="h-4 w-4" />
                      <AlertDescription className="ml-2">
                        Peak call volume occurs between <strong>10am - 2pm</strong>. Consider ensuring adequate staffing during these hours.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
